/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.14.0
 * @since 1.13.0
 */

import { Fragment, useCallback } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EmailInput } from '../../../components/common/EmailInput/EmailInput';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardExpensesDetail,
} from '../../../modules/internationalization/intSelectors';
import ICommonTranslation from '../../../modules/internationalization/types/common';
import { IWizardExpensesDetailTranslation } from '../../../modules/internationalization/types/wizards/expensesDetail';
import { WizardReportHeaderStyled } from '../../../modules/wizard/components/WizardReport/styled/WizardReportStyled';
import WizardReport from '../../../modules/wizard/components/WizardReport/WizardReport';
import { EWizardReportParam } from '../../../modules/wizard/components/WizardReport/WizardReportConstants';
import { WizardReportParam } from '../../../modules/wizard/components/WizardReport/WizardReportParam';
import { makeSelectWizardReportParam } from '../../../modules/wizard/components/WizardReport/wizardReportSelectors';
import { IWizardReportParam } from '../../../modules/wizard/components/WizardReport/wizardReportTypes';
import { IWizardSummaryParam, WizardSummary } from '../../../modules/wizard/components/WizardSummary/WizardSummary';
import { wizardReportSetParamAction } from '../../../modules/wizard/wizardActions';
import { IWizardReportSetParamPayload } from '../../../modules/wizard/wizardTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { formatDateToMMYYYY } from '../../../utils/formatText';
import { wizardExpensesDetailCheckValidationAction } from '../wizardExpensesDetailActions';
import { IWizardExpensesDetailReducer } from '../wizardExpensesDetailReducer';
import {
    makeSelectWizardExpensesDetailDate,
    makeSelectWizardExpensesDetailFormatsAvailableChosen,
    makeSelectWizardExpensesDetailHierarchyLevelsChosen,
} from '../wizardExpensesDetailSelectors';
import { WizardExpensesDetailStyled } from './styled/WizardExpensesDetailStyled';

const WizardExpensesDetailConfirmation = (props: IConnectedProps & IDispatchProps) => {

    const {
        date,
        formatsAvailableChosen,
        hierarchyLevelsChosen,
        reportDestEmail,
        translationCommon,
        translationExpensesDetails,

        checkValidation,
        setNotificationParam,
    } = props;

    const handleChange = useCallback((payload: IWizardReportSetParamPayload) => {
        setNotificationParam(payload);
        checkValidation();
    }, [checkValidation, setNotificationParam]);

    const expensesDetailSummaryParams: IWizardSummaryParam[] = [
        {
            title: translationExpensesDetails.summaryParams.formatsAvailable,
            values: [formatsAvailableChosen],
        },
        {
            title: translationExpensesDetails.summaryParams.date,
            values: [formatDateToMMYYYY(moment(date), translationCommon.calendar.months)],
        },
        {
            title: translationExpensesDetails.summaryParams.hierarchyLevels,
            values: [hierarchyLevelsChosen.label],
        },
    ];

    return (
        <WizardExpensesDetailStyled>
            <WizardReport
                notificationValidate={checkValidation}
            >
                <Fragment>
                    <WizardReportHeaderStyled>
                        {translationExpensesDetails.sendDocumentTitle}
                    </WizardReportHeaderStyled>

                    <WizardReportParam
                        dataName="wizard-report-param-email"
                        isEnabled={reportDestEmail.isEnabled}
                        label={translationExpensesDetails.reports.destEmailForLink}
                        setEnabled={(isEnabled: boolean) => handleChange({
                            paramType: EWizardReportParam.EMAIL_DEST, isEnabled,
                        })}
                    >
                        <EmailInput
                            dataName="wizard-report-param-email-input"
                            emailInputValue={reportDestEmail.value}
                            isValid={reportDestEmail.isValid}
                            translation={translationCommon}
                            onEmailInputChange={(value: string) => handleChange({
                                paramType: EWizardReportParam.EMAIL_DEST, value,
                            })}
                            onEmailInputValidate={(isValid: boolean) => handleChange({
                                paramType: EWizardReportParam.EMAIL_DEST, isValid,
                            })}
                        />
                    </WizardReportParam>
                </Fragment>

            </WizardReport>
            <div>
                <WizardSummary params={expensesDetailSummaryParams}/>
            </div>
        </WizardExpensesDetailStyled>
    );
};

export interface IConnectedProps extends Pick<IWizardExpensesDetailReducer,
    'date' | 'formatsAvailableChosen' | 'hierarchyLevelsChosen'> {
    reportDestEmail: IWizardReportParam;
    translationCommon: ICommonTranslation;
    translationExpensesDetails: IWizardExpensesDetailTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    date: makeSelectWizardExpensesDetailDate(),
    formatsAvailableChosen: makeSelectWizardExpensesDetailFormatsAvailableChosen(),
    hierarchyLevelsChosen: makeSelectWizardExpensesDetailHierarchyLevelsChosen(),
    reportDestEmail: makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST),
    translationCommon: makeSelectTranslationCommon(),
    translationExpensesDetails: makeSelectTranslationWizardExpensesDetail(),
});

export interface IDispatchProps {
    checkValidation: () => void;
    setNotificationParam: (payload: IWizardReportSetParamPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    checkValidation: () => dispatch(wizardExpensesDetailCheckValidationAction()),
    setNotificationParam: payload => dispatch(wizardReportSetParamAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardExpensesDetailConfirmation);
